import React, { memo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import MobileLayout from '../../../../components/MobileLayout'
import PodcastDetailsLayout from '../../../../components/PodcastDetailsLayout'
import Text from '../../../../components/atoms/Text'
import Button from '../../../../components/atoms/GoodFlipButton'
import ArrowIcon from '../../../../components/atoms/Arrow'
import ZydusHeader from '../../../../components/molecules/ZydusHeader'
import CancelIcon from '../../../../assets/icons/cancel_icon.svg'
import ZydusLogo from '../../../../assets/images/zydus_logo.webp'
import BlueVectorBG from '../../../../assets/images/blue_vector_bg.webp'
import { PODCAST_DATA } from '../../../../constants/podcastData'
import { Footer } from '../index'

const PodcastDetailsPage = () => {
  const navigate = useNavigate()
  const { podcastId } = useParams()
  const [youtubeVideoId, setYoutubeVideoId] = useState(null)
  const podcast = PODCAST_DATA.find((item) => item.videoKey === podcastId)

  const onWatchPodcast = () => {
    if (!podcast?.videoKey) return
    setYoutubeVideoId(podcast.videoKey)
  }

  const handleClose = () => {
    setYoutubeVideoId(null)
  }

  const getYoutubeEmbedSrc = (videoId) => {
    if (!videoId) return null
    const params = new URLSearchParams({
      autoplay: '1',
      playsinline: '1',
      rel: '0',
      modestbranding: '1',
    })

    if (typeof window !== 'undefined' && window.location?.origin) {
      params.set('origin', window.location.origin)
    }

    return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`
  }

  if (!podcast) {
    return (
      <MobileLayout>
        <div>
          <ZydusHeader />
          <PodcastDetailsLayout>
            <div className="w-full pt-20 lg:pt-28 pb-10 bg-gradient-to-t to-[#EEFFFF] from-[#FFFFFF]">
              <div className="relative min-h-[320px] lg:min-h-[427px] flex flex-col items-center justify-center gap-2 px-4 lg:px-8">
                <img
                  src={BlueVectorBG}
                  alt="blue-vector-bg"
                  className="absolute top-0 left-0 w-full h-full object-cover z-0"
                />
                <img
                  src={ZydusLogo}
                  alt="zydus-logo"
                  className="relative z-10 w-20 h-11"
                />
                <Text
                  variant="display"
                  size="small"
                  tag="p"
                  className="!text-center !text-[#199EA5] z-10 !text-[36px] !leading-[40px] lg:!text-[40px] lg:!leading-[44px]"
                >
                  Podcast not found
                </Text>
                <Text
                  variant="body"
                  size="large"
                  tag="p"
                  className="!text-center z-10 !text-text-secondary max-w-2xl"
                >
                  This podcast link may be invalid or no longer available.
                </Text>
                <button
                  className="relative z-10 mt-4 bg-[#199EA5] text-white px-5 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
                  onClick={() => navigate('/')}
                >
                  Back to Home <ArrowIcon direction="right" />
                </button>
              </div>
            </div>
            <Footer />
          </PodcastDetailsLayout>
        </div>
      </MobileLayout>
    )
  }

  return (
    <MobileLayout>
      <div>
        <ZydusHeader />
        <PodcastDetailsLayout>
          <div className="lg:px-8 pb-10 w-full mx-auto">
            <div className="relative overflow-hidden">
              <img
                src={podcast.image}
                alt={podcast.title}
                className="w-full h-[440px] md:h-[400px] lg:h-[560px] object-fill"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/25 p-5 lg:p-10 flex flex-col justify-end">
                <Text
                  variant="display"
                  size="small"
                  tag="p"
                  className="!text-white max-w-3xl !text-[28px] !leading-[32px] lg:!text-[40px] lg:!leading-[44px]"
                >
                  {podcast.title}
                </Text>
                <div className="flex gap-1 items-center mt-2">
                  <Text variant="body" size="small" className="!text-[#D8D8E0]">
                    {podcast.date}
                  </Text>
                  <span className="!text-[#E2E2EA] font-extralight">|</span>
                  <Text variant="body" size="small" className="!text-[#D8D8E0]">
                    {podcast.time}
                  </Text>
                </div>
                <button
                  className="mt-6 bg-[#199EA5] text-white rounded-2 px-4 py-2.5 w-fit text-sm font-semibold flex items-center gap-1"
                  onClick={onWatchPodcast}
                >
                  Watch Podcast <ArrowIcon direction="right" />
                </button>
              </div>
            </div>

            <div className="mt-8 lg:mt-12 p-4 lg:p-6 flex flex-col lg:flex-row items-stretch gap-5 lg:gap-6 lg:mx-12 justify-center">
              <div className="w-full lg:w-[38%] lg:self-stretch">
                <div
                  className="w-full h-full min-h-[320px] lg:min-h-0 relative overflow-hidden rounded-2xl border border-[#E3EAEE] cursor-pointer flex items-center justify-center"
                  onClick={onWatchPodcast}
                >
                  <img
                    src={podcast.image}
                    alt={`${podcast.title} statement`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="w-full lg:w-[40%] flex flex-col justify-center">
                <Text
                  variant="title"
                  size="large"
                  tag="p"
                  className="!text-[#3D4854]"
                >
                  {podcast.statementTitle}
                </Text>
                <Text
                  variant="body"
                  size="large"
                  tag="p"
                  className="mt-2 !text-foreground-quaternary"
                >
                  {podcast.statementDescription}
                </Text>
                <div className="hidden lg:block mt-2 lg:max-w-xs">
                  <Button
                    title="Watch Podcast"
                    onClick={onWatchPodcast}
                    type="goodflip_primary"
                    className="!m-0 !mt-3"
                    rightIcon={<ArrowIcon direction="right" />}
                  />
                </div>
                <button
                  className="lg:hidden mt-5 bg-[#199EA5] text-white rounded-2 px-4 py-2.5 w-fit text-xs lg:text-sm font-semibold"
                  onClick={onWatchPodcast}
                >
                  WATCH PODCAST
                </button>
              </div>
            </div>
          </div>
        </PodcastDetailsLayout>
        <Footer />
      </div>

      {youtubeVideoId && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-[2000] flex items-center justify-center">
          <div
            className="absolute top-4 right-4 z-50 cursor-pointer bg-white p-0.5 border rounded-full"
            onClick={handleClose}
          >
            <img src={CancelIcon} alt="close" />
          </div>
          <div className="relative w-full max-w-5xl aspect-video mx-4">
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={getYoutubeEmbedSrc(youtubeVideoId)}
              title="youtube video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="origin-when-cross-origin"
            ></iframe>
          </div>
        </div>
      )}
    </MobileLayout>
  )
}

export default memo(PodcastDetailsPage)
